import { type RefObject, useEffect, useRef } from "react";

import { allTrackers } from "app/services/track/track";

const TABLE_SETTINGS_STORAGE_KEY = "table_settings";
const COLUMN_PICKER_BUTTON_AUTOMATION = "list-header-columns-configure-button";

type StoredColumnState = Record<string, boolean>; // key → visible

function readColumnState(tableKey: string): StoredColumnState | null {
    try {
        const stored = localStorage.getItem(TABLE_SETTINGS_STORAGE_KEY);
        if (!stored) return null;
        const parsed = JSON.parse(stored) as Record<string, { order?: { key: string; hidden: boolean }[] }>;
        const order = parsed[tableKey]?.order;
        if (!order) return null;
        return Object.fromEntries(order.map((c) => [c.key, !c.hidden]));
    } catch {
        return null;
    }
}

/**
 * Tracks column picker interactions for a given table instance.
 *
 * - Open: fires when the user clicks the column picker button (detected via
 *   data-automation="list-header-columns-configure-button" inside the container).
 * - Toggle: fires for each column whose visibility changed when the user clicks
 *   Apply (detected by intercepting localStorage.setItem for "table_settings").
 */
export const useColumnPickerTracking = (
    tableKey: string,
    subName: string,
    containerRef: RefObject<HTMLElement>,
): void => {
    const prevColumnStateRef = useRef<StoredColumnState | null>(null);

    // Seed the initial column state so the first Apply can diff correctly
    useEffect(() => {
        prevColumnStateRef.current = readColumnState(tableKey);
    }, [tableKey]);

    // Track "Open" via click delegation on the column picker button
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest(`[data-automation="${COLUMN_PICKER_BUTTON_AUTOMATION}"]`)) {
                allTrackers.dispatchTrackEvent("Column Picker", "Open", subName);
            }
        };

        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
    }, [subName, containerRef]);

    // Track "Toggle" by intercepting localStorage.setItem for the table settings key.
    // We maintain a proper interception chain so multiple mounted instances don't conflict.
    useEffect(() => {
        const prevSetItem = Storage.prototype.setItem;

        function interceptedSetItem(this: Storage, key: string, value: string) {
            prevSetItem.call(this, key, value);
            if (key !== TABLE_SETTINGS_STORAGE_KEY) return;

            try {
                const parsed = JSON.parse(value) as Record<string, { order?: { key: string; hidden: boolean }[] }>;
                const order = parsed[tableKey]?.order;
                if (!order) return;

                const newState = Object.fromEntries(order.map((c) => [c.key, !c.hidden]));
                const prev = prevColumnStateRef.current;

                if (prev) {
                    for (const [colKey, visible] of Object.entries(newState)) {
                        if (prev[colKey] !== visible) {
                            allTrackers.dispatchTrackEvent(
                                "Column Picker",
                                "Toggle",
                                `${subName}/${colKey}:${visible ? "show" : "hide"}`,
                            );
                        }
                    }
                }

                prevColumnStateRef.current = newState;
            } catch {
                // ignore parse errors
            }
        }

        Storage.prototype.setItem = interceptedSetItem;

        return () => {
            // Only unwind our layer — don't break a chain set by another instance
            if ((Storage.prototype.setItem as unknown) === interceptedSetItem) {
                Storage.prototype.setItem = prevSetItem;
            }
        };
    }, [tableKey, subName]);
};
